import { TFunction } from "i18next";
import { SessionTemplate } from "../../../session_templates";
import { SessionAnalysis } from "../../domain/Session";
import { buildTemplateHTML, parseTemplateHTML } from "../utils/templateDocumentUtils";
import { blockNoteToHtml } from "../../../../shared/utils/blockNote";

/**
 * Strip legacy raw `gs://` storage URIs from constellation session notes that
 * older closes persisted (e.g. "Snapshot: gs://bucket/…"). The snapshots are
 * now shown as images via signed URLs, so the bare URIs are noise. Operates on
 * the raw notes string regardless of format (plain text / HTML).
 */
export function stripStorageUris(notes: string): string {
    if (!notes) return notes;
    return notes
        // Whole "Snapshot: gs://…" lines.
        .replace(/^[ \t]*snapshot:\s*gs:\/\/\S+[ \t]*\r?\n?/gim, '')
        // Any other bare gs:// URI left inline.
        .replace(/gs:\/\/\S+/gi, '')
        // Collapse blank lines the removals may leave behind.
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

/**
 * Wrap plain text (e.g. OCR output) in paragraphs so the editor keeps its line
 * breaks — a bare `\n` is collapsed to a space by the HTML parser. Strings that
 * already contain markup are returned untouched.
 */
export function plainTextToHtml(text: string): string {
    if (!text) return '';
    if (/<[a-z][\s\S]*>/i.test(text)) return text;

    const escape = (s: string) =>
        s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    return text
        .split(/\r?\n/)
        .map(line => (line.trim() ? `<p>${escape(line)}</p>` : '<p></p>'))
        .join('');
}

/** Convert BlockNote JSON or plain HTML string to an HTML string. */
export function notesToHtml(notes: string): string {
    return blockNoteToHtml(notes);
}

/**
 * Resolve the HTML to display.
 * Priority:
 *  1. template_data from analysis  → rebuild unified doc
 *  2. notes that look like HTML   → render directly (new unified format)
 *  3. legacy **Field**: markdown  → convert to headings for display
 *  4. plain text/notes            → render as-is
 */
export function resolveNotesHtml(
    notes: string,
    template?: SessionTemplate | null,
    analysis?: SessionAnalysis,
    t?: TFunction,
    lng?: string
): string {
    if (!notes && !analysis?.template_data?.length) return "";

    // Convert BlockNote JSON to HTML first so all downstream checks see HTML
    const resolvedNotes = notesToHtml(notes);

    const fields = template?.fields ?? [];

    // 1. Notes that are already a unified HTML document (contains our H2 with data-field-id)
    // Priority #1 because this preserves exact rich text formatting from the editor.
    if (resolvedNotes && resolvedNotes.includes('data-field-id')) {
        return resolvedNotes;
    }

    // 2. Prefer structured template_data if notes are missing or legacy
    if (fields.length > 0 && analysis?.template_data?.length) {
        const values: Record<string, string> = {};
        fields.forEach(field => {
            const data = analysis.template_data?.find(d =>
                d.template_field_id === field.id || d.template_field_id === field.name
            );
            if (data?.value) values[field.id!] = data.value;
        });
        if (Object.keys(values).length > 0) {
            return buildTemplateHTML(fields, values, t, lng);
        }
    }

    // 3. Template present but notes are in legacy **Field**: format
    if (fields.length > 0 && resolvedNotes) {
        const parsed = parseTemplateHTML(resolvedNotes, fields);
        const hasContent = Object.values(parsed).some(v => v && v !== "<p></p>");
        if (hasContent) {
            return buildTemplateHTML(fields, parsed, t, lng);
        }
        // Try legacy markdown **Bold**: pattern
        const hasMarkdown = /\*\*[^*]+\*\*/.test(resolvedNotes);
        if (hasMarkdown) {
            const values: Record<string, string> = {};
            fields.forEach(field => {
                const esc = field.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const re = new RegExp(`\\*\\*${esc}\\*\\*\\s*:?`, 'i');
                const m = resolvedNotes.match(re);
                if (m && m.index !== undefined) {
                    const start = m.index + m[0].length;
                    let end = resolvedNotes.length;
                    fields.forEach(nf => {
                        if (nf.name === field.name) return;
                        const ne = nf.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                        const nm = resolvedNotes.substring(start).match(new RegExp(`\\*\\*${ne}\\*\\*\\s*:?`, 'i'));
                        if (nm?.index !== undefined && start + nm.index < end) end = start + nm.index;
                    });
                    values[field.id!] = resolvedNotes.substring(start, end).trim();
                } else {
                    values[field.id!] = "";
                }
            });
            return buildTemplateHTML(fields, values, t, lng);
        }
    }

    // 4. Plain notes or already HTML
    if (!resolvedNotes) return "";

    const isHtml = /<[a-z][\s\S]*>/i.test(resolvedNotes);
    if (isHtml) {
        return resolvedNotes;
    }

    return resolvedNotes
        .replace(/\*\*(.*?)\*\*\s*:?/g, '<h2>$1</h2>')
        .replace(/\n{2,}/g, '</p><p>')
        .replace(/\n/g, '<br />');
}
